import { useStepper } from '@pelckmans/elementis/components/stepper';
import { createContext, Dispatch, ReactNode, SetStateAction, useEffect, useMemo, useState } from 'react';
import { linkColumnOptions } from '../hooks/useImportMemberColumns';
import { useTranslation } from 'react-i18next';
import { RegisterMemberRequestPayload } from '../../../../../../api/my-groups';
import { makeUsernameUnique } from '../../../../../../utils/voStudents';
import {
  validateMemberEmail,
  validateMemberFirstName,
  validateMemberLastName,
  validateMemberPassword,
  validateMemberUsername,
} from '../../utils/memberValidations';

export const Steps = {
  FILE_IMPORT: 'file-import',
  COLUMN_MAPPING: 'column-mapping',
  DATA_VERIFICATION: 'data-verification',
} as const;

export const ErrorFields = {
  UPLOAD_FIELD: 'UPLOAD_FIELD',
} as const;

const REQUIRED_COLUMN_MAPPING = [
  linkColumnOptions.FIRST_NAME,
  linkColumnOptions.LAST_NAME,
  linkColumnOptions.PASSWORD,
] as const;

export type AlertType =
  | 'RETRY'
  | 'INVALID'
  | 'NO_REQUIRED_COLUMNS'
  | 'NO_ROWS_SELECTED'
  | 'INVALID_DATA_WARNING'
  | 'SELECTED_INVALID_ROWS'
  | undefined;

export interface ImportMembersContextValue {
  fileName?: string;
  fileHeaderData?: string[];
  fileMembersData?: string[][];
  errors?: Record<string, string | undefined>;
  alertType?: AlertType;
  columnMapping?: Record<string, string> | undefined;
  columnMappingByLinkedOption?: Record<string, string> | undefined;
  stepperProgress: Record<string, boolean>;
  toBeAddedMembers: RegisterMemberRequestPayload[];
  selection: Set<number>;
  existingUsernames: string[];
  setFileName: Dispatch<SetStateAction<string | undefined>>;
  setFileData: Dispatch<SetStateAction<string[][] | undefined>>;
  setErrors: Dispatch<SetStateAction<Record<string, string | undefined> | undefined>>;
  setAlertType: Dispatch<SetStateAction<AlertType>>;
  setColumnMapping: Dispatch<SetStateAction<Record<string, string> | undefined>>;
  goToNextStep: (currentStep: string) => void;
  setSelection: Dispatch<SetStateAction<Set<number>>>;
  setToBeAddedMembers: Dispatch<SetStateAction<RegisterMemberRequestPayload[]>>;
}

const defaultImportMembersContextValue: ImportMembersContextValue = {
  fileName: '',
  fileHeaderData: [],
  fileMembersData: [],
  toBeAddedMembers: [],
  selection: new Set<number>(),
  existingUsernames: [],
  setFileName: () => {},
  setFileData: () => {},
  setErrors: () => {},
  setAlertType: () => {},
  setColumnMapping: () => {},
  goToNextStep: (currentStep: string) => {},
  setSelection: () => {},
  setToBeAddedMembers: () => {},
  stepperProgress: {
    [Steps.FILE_IMPORT]: false,
    [Steps.COLUMN_MAPPING]: false,
    [Steps.DATA_VERIFICATION]: false,
  },
};

export const ImportMembersContext = createContext<ImportMembersContextValue>(defaultImportMembersContextValue);

function isAllRequiredColumnsMapped(columnMapping?: Record<string, string>) {
  if (!columnMapping) return false;
  const mappedColumnsValues = Object.values(columnMapping);
  return REQUIRED_COLUMN_MAPPING.every(requiredColumn => mappedColumnsValues.includes(requiredColumn));
}

function createUsernameHashMap(
  fileData?: string[][],
  columnMappingByLinkedOption?: Record<string, string>,
  existingUsernames: string[] = [],
) {
  const usernameMap = new Map<number, string>();

  if (!fileData || !columnMappingByLinkedOption) return usernameMap;

  const hasUsernameColumn = Boolean(columnMappingByLinkedOption?.[linkColumnOptions.USERNAME] !== undefined);

  fileData?.forEach((row, index) => {
    const usernameColumnIndex = columnMappingByLinkedOption?.[linkColumnOptions.USERNAME];
    let username = usernameColumnIndex !== undefined ? row[Number(usernameColumnIndex)] : '';
    if (!username && !hasUsernameColumn) {
      const firstName = row[Number(columnMappingByLinkedOption?.[linkColumnOptions.FIRST_NAME])];
      const lastName = row[Number(columnMappingByLinkedOption?.[linkColumnOptions.LAST_NAME])];
      if (!firstName || !lastName) return;

      username = makeUsernameUnique(firstName, lastName, Array.from(usernameMap.values()).concat(existingUsernames));
    }
    usernameMap.set(index, username);
  });
  return usernameMap;
}

export default function ImportMembersContextProvider({
  children,
  existingUsernames,
}: {
  children: ReactNode;
  existingUsernames: string[];
}) {
  const { t } = useTranslation();
  const { goToNextStep } = useStepper();

  const [fileName, setFileName] = useState<string>();
  const [fileData, setFileData] = useState<string[][]>();
  const [fileHeaderData, setFileHeaderData] = useState<string[]>();
  const [fileMembersData, setFileMembersData] = useState<string[][]>();
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>();
  const [toBeAddedMembers, setToBeAddedMembers] = useState<RegisterMemberRequestPayload[]>([]);
  const [errors, setErrors] = useState<Record<string, string | undefined> | undefined>();
  const [alertType, setAlertType] = useState<AlertType>();
  const [selection, setSelection] = useState<Set<number>>(new Set());

  const stepperProgress: Record<string, boolean> = useMemo(
    () => ({
      [Steps.FILE_IMPORT]: Boolean(fileData),
      [Steps.COLUMN_MAPPING]: isAllRequiredColumnsMapped(columnMapping),
    }),
    [fileData, columnMapping],
  );

  const columnMappingByLinkedOption = useMemo(
    () =>
      columnMapping
        ? Object.entries(columnMapping).reduce((acc, [columnIndex, linkedOption]) => {
            if (linkedOption) {
              acc[linkedOption] = columnIndex;
            }
            return acc;
          }, {} as Record<string, string>)
        : undefined,
    [columnMapping],
  );

  useEffect(() => {
    setAlertType(undefined);
    setColumnMapping(undefined);
    if (fileData?.length) {
      setFileHeaderData(fileData?.[0]);
      setFileMembersData(fileData?.slice(1));
    }
  }, [fileData]);

  useEffect(() => {
    if (stepperProgress[Steps.COLUMN_MAPPING]) {
      const usernameMap = createUsernameHashMap(fileMembersData, columnMappingByLinkedOption, existingUsernames);
      const members = fileMembersData?.map((row, index) => {
        const email = row[Number(columnMappingByLinkedOption?.[linkColumnOptions.EMAIL])];
        const firstName = row[Number(columnMappingByLinkedOption?.[linkColumnOptions.FIRST_NAME])];
        const lastName = row[Number(columnMappingByLinkedOption?.[linkColumnOptions.LAST_NAME])];
        const password = row[Number(columnMappingByLinkedOption?.[linkColumnOptions.PASSWORD])];
        const username = usernameMap.get(index) || '';

        return {
          firstName,
          lastName,
          password,
          username,
          ...(email ? { email } : {}),
        };
      });

      setToBeAddedMembers(members || []);
    }
  }, [columnMappingByLinkedOption, existingUsernames, fileMembersData, stepperProgress]);

  useEffect(() => {
    if (toBeAddedMembers.length > 0) {
      setSelection(() => new Set(toBeAddedMembers.map((_, index) => index)));
    }
  }, [toBeAddedMembers]);

  useEffect(() => {
    const errors: Record<string, string | undefined> = {};
    toBeAddedMembers?.forEach((member, index) => {
      const { firstName, lastName, password, username, email } = member;
      const error =
        validateMemberFirstName(firstName) ||
        validateMemberLastName(lastName) ||
        validateMemberUsername(username, existingUsernames) ||
        validateMemberPassword(password) ||
        validateMemberEmail(email);
      if (error) {
        errors[`${index}`] = error;
      }
    });

    if (Object.keys(errors).length > 0) {
      setErrors(errors);
    } else {
      setErrors(undefined);
    }
  }, [existingUsernames, toBeAddedMembers]);

  async function onNextStep(currentStep: string) {
    if (currentStep === Steps.FILE_IMPORT && !stepperProgress[Steps.FILE_IMPORT]) {
      setAlertType('INVALID');
      setErrors({ [ErrorFields.UPLOAD_FIELD]: t('FORM.VALIDATION.REQUIRED') });
      return;
    }

    if (currentStep === Steps.COLUMN_MAPPING && !stepperProgress[Steps.COLUMN_MAPPING]) {
      setAlertType('NO_REQUIRED_COLUMNS');
      return;
    }

    // Data verification submit is handled in the ImportMembersModal component
    if (currentStep === Steps.DATA_VERIFICATION) {
      return;
    }

    goToNextStep();
  }

  return (
    <ImportMembersContext.Provider
      value={{
        fileName,
        fileHeaderData,
        fileMembersData,
        errors,
        alertType,
        columnMapping,
        columnMappingByLinkedOption,
        toBeAddedMembers: toBeAddedMembers,
        selection,
        existingUsernames,
        setFileName,
        setFileData,
        setErrors,
        setAlertType,
        setColumnMapping,
        goToNextStep: onNextStep,
        setSelection,
        setToBeAddedMembers: setToBeAddedMembers,
        stepperProgress,
      }}
    >
      {children}
    </ImportMembersContext.Provider>
  );
}
